import {Observable} from 'rxjs';
import {DataSource} from '@mominsamir/ngx-smart-table/lib/lib/data-source/data-source';
import {
    Filter,
    FilterFieldOption,
    FilterFieldType,
    GenericListRequest,
    PagingResponse,
    TableFilterConfig,
} from '@core/interfaces/system/system-common';
import {APIResponse, FilterFieldOptionRequest, FilterFieldOptionResponse} from '@core/interfaces/system/system-common';
import {OrderByDirection} from '@core/interfaces/engin/mobile-admin/group-inspection-values';

export enum PublicationStatus {
    PUBLISHED = 'PUBLISHED',
    READY_FOR_PUBLISH = 'READY_FOR_PUBLISH',
    HAS_WARNINGS = 'HAS_WARNINGS',
    DELETED = 'DELETED',
    DRAFT = 'DRAFT',
}

export interface GroupInspection {
    id: number;
    groupLabel: string;
    groupIcon: string;
    description: string | null;
    createdOn: string;
    updatedOn: string;
    countSubgroups: number;
    countContents: number;
    status: 'PUBLISHED' | 'ERRORS_FOUND' | 'READY_TO_PUBLISH';
    autoRefreshAvailable: boolean;
}

/**
 * Represents a subgroup within a group inspection
 */
export interface Subgroup {
    id: number;
    tempId?: string;
    subgroupLabel: string;
    orderByDirection: string;
    orderByField: string;
    status: string;
    createdOn: string;
    updatedOn: string;
    autoRefreshAvailable: boolean;
    countContent: number;
    sortOrder: number;
    fieldValue: string;
    selectedFormIds?: number[];
    subgroupContentList: SubgroupContentResponseDto[];
}

/**
 * DTO interfaces matching backend structure
 */
export interface GroupInspectionContentDetailDto {
    groupContentId: number;
    formId: number;
    formCode: string;
    formName: string;
    formDescription: string;
}

export interface SubgroupContentResponseDto {
    groupInspectionValueId: number;
    assetId: string;
    sortOrder: number;
    formList: GroupInspectionContentDetailDto[];
}

/**
 * Configuration data for subgroups
 */
export interface SubgroupConfiguration {
    groupInspectionId: string;
    groupInspectionLabel: string;
    subgroups: Subgroup[];
    selectedSubgroups: Subgroup[];
}

/**
 * Response DTO for group inspection operations
 */
export interface GroupResponseDto {
    id: number;
    groupLabel: string;
    groupIcon: string;
    description: string | null;
    createdOn: string;
    updatedOn: string;
    countSubgroups: number;
    countContents: number;
    status: 'PUBLISHED' | 'ERRORS_FOUND' | 'READY_TO_PUBLISH';
    autoRefreshAvailable: boolean;
}

/**
 * Response DTO for subgroup operations
 */
export interface SubgroupResponseDto {
    id: number;
    subgroupLabel: string;
    orderByDirection: string;
    orderByField: string;
    status: string;
    createdOn: string;
    updatedOn: string;
    autoRefreshAvailable: boolean;
    countContent: number;
    sortOrder: number;
    fieldValue: string;
    subgroupContentList: SubgroupContentResponseDto[];
}

export interface SubgroupCreateRequestDto {
    save: boolean;
    subgroupCreateDtoList: SubgroupCreateDto[];
}

/**
 * DTO for creating a new subgroup
 */
export interface SubgroupCreateDto {
    tempId?: string;
    subgroupLabel: string;
    groupValues: string[];
    orderByField: string;
    orderByDirection: OrderByDirection;
    formIds: number[];
}

/**
 * DTO for merging subgroups
 */
export interface SubgroupMergeRequestDto {
    subgroupMergeDtos: SubgroupMergeDto[];
    saveFlag: boolean;
}
/**
 * DTO for merging subgroups
 */
export interface SubgroupMergeDto {
    subgroupLabel: string;
    subgroupIds: number[];
}

/**
 * DTO for Edit subgroups request
 */
export interface SubgroupEditRequestDto {
    subgroupEditDtoList: SubgroupEditDto[];
    saveFlag: boolean;
}
/**
 * DTO for Edit subgroups
 */
export interface SubgroupEditDto {
    subgroupLabel: string;
    subgroupId: number;
    formIds: number[];
    groupValues: string[];
}

/**
 * Icon interface for icon selection
 */
export interface Icon {
    id: string;
    name: string;
    pack: 'eva' | 'engin';
}

/**
 * Validation errors for form fields
 */
export interface ValidationErrors {
    label?: string;
    description?: string;
    icon?: string;
    assetScope?: string;
    groupBy?: string;
}

/**
 * Group-by option interface
 */
export interface GroupByOption {
    key: string;
    label: string;
}

/**
 * Form-specific data structure for creating/editing group inspections
 */
export interface GroupInspectionFormData {
    groupLabel: string;
    description: string;
    groupIcon: string;
    assetScope: Filter[];
    groupByField: string;
    formIdList: number[];
}
/**
 * Form-specific data structure for creating/editing group inspections
 */
export interface GroupInspectionFormDataResponse {
    id: number;
    groupLabel: string;
    description: string;
    groupIcon: string;
    groupScope: Filter[];
    groupByField: string;
    formIds: number[];
}
/**
 * Interface for sort option
 */

export interface FormListRequest extends GenericListRequest {
    columns: string[];
}
export interface FormListDto {
    createdDate: string;
    description: string;
    formId: number;
    formName: string;
    userName: string;
}
export enum ViewMode {
    CREATE = ' CREATE',
    EDIT = 'EDIT',
    VIEW = 'VIEW',
}

export interface SortOption {
    fieldKey: string;
    fieldName?: string;
    selected?: boolean;
    required?: boolean;
    fieldType?: FilterFieldType;
    disabled?: boolean;
    charLimit?: number; //only for string types
}
/**
 * Subgroup actions tracking interface
 */
export interface SubgroupActions {
    createSubgroups: SubgroupCreateRequestDto[];
    editSubgroups: SubgroupEditRequestDto[];
    deleteSubgroups: Subgroup[];
    mergeSubgroups: SubgroupMergeRequestDto[];
}

export abstract class GroupInspectionsService {
    /**
     * Gets a ServerDataSource configured for group inspection data
     * @returns Observable of ServerDataSource for the group inspection data
     */
    abstract getGroupInspectionData(): Observable<DataSource>;

    /**
     * Toggles the publish status of a group inspection
     * @param groupId The ID of the group inspection
     * @param published The new published status
     * @returns Observable of GroupResponseDto
     */
    abstract togglePublishStatus(groupId: number, published: boolean): Observable<GroupResponseDto>;

    /**
     * Gets the list of subgroups for a group inspection
     * @param groupId The ID of the group inspection
     * @returns Observable of array of SubgroupResponseDto
     */
    abstract listSubgroups(groupId: number): Observable<SubgroupResponseDto[]>;

    /**
     * Creates a new group inspection
     * @param data The group inspection data to create
     * @returns Observable of GroupResponseDto
     */
    abstract createGroupInspection(data: GroupInspectionFormData): Observable<APIResponse<GroupInspection>>;

    /**
     * Edits an existing group inspection
     * @param groupId The ID of the group inspection
     * @param data The updated group inspection data
     * @returns Observable of GroupResponseDto
     */
    abstract editGroupInspection(groupId: number, data: Partial<GroupInspection>): Observable<GroupResponseDto>;

    /**
     * Deletes a group inspection
     * @param groupId The ID of the group inspection
     * @returns Observable of void
     */
    abstract deleteGroupInspection(groupId: number): Observable<void>;

    /**
     * Refreshes all groups
     * @returns Observable of void
     */
    abstract refreshAllGroups(): Observable<void>;

    /**
     * Refreshes a specific group
     * @param groupId The ID of the group inspection
     * @returns Observable of void
     */
    abstract refreshGroup(groupId: number): Observable<void>;

    /**
     * Saves the configuration for a group inspection
     * Commits configuration and updates status to READY_FOR_PUBLISH or HAS_WARNINGS
     * @param groupId The ID of the group inspection
     * @returns Observable of GroupResponseDto
     */
    abstract saveConfiguration(groupId: number): Observable<GroupResponseDto>;

    /**
     * Creates a new subgroup inside the given inspection group
     * @param groupId The ID of the group inspection
     * @param data The subgroup data to create
     * @returns Observable of SubgroupResponseDto
     */
    abstract createSubgroup(
        groupId: number,
        data: SubgroupCreateRequestDto,
    ): Observable<APIResponse<SubgroupResponseDto[]>>;

    /**
     * Update subgroup inside the given inspection group
     * @param groupId The ID of the group inspection
     * @param data The subgroup data to create
     * @returns Observable of SubgroupResponseDto
     */
    abstract updateSubgroup(
        groupId: number,
        data: SubgroupEditRequestDto,
    ): Observable<APIResponse<SubgroupResponseDto[]>>;

    /**
     * Merges multiple subgroups into one
     * @param groupId The ID of the group inspection
     * @param data The merge data containing target and source subgroups
     * @returns Observable of void
     */
    abstract mergeSubgroups(
        groupId: number,
        data: SubgroupMergeRequestDto,
    ): Observable<APIResponse<SubgroupResponseDto[]>>;

    /**
     * Removes a group only if it's in DRAFT status
     * @param groupId The ID of the group inspection
     * @returns Observable of void
     */
    abstract deleteDraftGroup(groupId: number): Observable<void>;

    /**
     * Checks if the given group label already exists
     * @param groupLabel The group label to verify
     * @returns Observable of boolean (true if available, false if exists)
     */
    abstract verifyGroupLabelAvailability(groupLabel: string): Observable<APIResponse<boolean>>;

    /**
     * Get available filter configurations
     */
    abstract getGenericFilters(): Observable<APIResponse<TableFilterConfig[]>>;

    /**
     * Get filter field options dynamically
     * @param body - Request body with field key and filter params
     */
    abstract getFilterFieldOptions(body: FilterFieldOptionRequest): Observable<APIResponse<FilterFieldOptionResponse>>;
    /**
     * Get group inspection details by ID
     * @param groupId - Group inspection ID
     */
    abstract getGroupInspection(groupId: number): Observable<APIResponse<GroupInspectionFormDataResponse>>;
    /**
     * Update existing group inspection
     * @param groupId - Group inspection ID
     * @param data - Updated form data
     */
    abstract updateGroupInspection(
        groupId: number,
        data: GroupInspectionFormData,
    ): Observable<APIResponse<GroupInspection>>;

    /**
     * Get available icons for selection
     */
    abstract getAvailableIcons(): Observable<APIResponse<string[]>>;

    /**
     * Get group-by options (asset class grouping options)
     * @returns Observable of APIResponse containing array of GroupByOption
     */
    abstract getGroupByOptions(): Observable<APIResponse<GroupByOption[]>>;

    /**
     * Get subgroup options for a specific group inspection
     * Returns available grouping options that can be used to create subgroups
     * @param groupId - The ID of the group inspection
     * @returns Observable of APIResponse containing array of GroupByOption
     */
    abstract getSubgroupOptions(groupId: number): Observable<APIResponse<GroupByOption[]>>;

    /**
     * Get available sort options for subgroup content
     * Returns sorting options that can be applied to assets within a subgroup
     * @param groupInspectionId - The ID of the group inspection
     * @param subgroupId - The ID of the subgroup
     * @returns Observable of APIResponse containing array of SortOption
     */
    abstract getSortOptions(groupInspectionId: number, subgroupId: number): Observable<APIResponse<SortOption[]>>;

    /**
     * Gets a ServerDataSource configured for inspection forms data
     * Returns all available published inspection forms that can be assigned to groups
     * @param groupInspectionId - Optional group inspection ID to filter forms
     * @param subgroupName - Optional subgroup name to filter forms
     * @returns Observable of DataSource for the inspection forms table
     */
    abstract getInspectionFormsDataSource(groupInspectionId?: number, subgroupName?: string): Observable<DataSource>;

    /**
     * Gets selected inspection forms data by their IDs
     * Retrieves detailed information for a specific set of forms (used for pre-selection)
     * @param selectedId - Array of form IDs to retrieve
     * @returns Observable of PagingResponse containing FormListDto array
     */
    abstract getSelectedInspectionFormsDataSource(selectedId: number[]): Observable<PagingResponse<FormListDto>>;

    /**
     * Get filter options for a specific field with search capability
     * Used for dynamic loading of filter dropdown options based on user search
     * @param fieldKey - The field key to get options for (e.g., 'form_name', 'user_name')
     * @param searchString - Search string to filter options
     * @returns Observable of APIResponse containing array of FilterFieldOption
     */
    abstract getFilterOptions(fieldKey: string, searchString: string): Observable<APIResponse<FilterFieldOption[]>>;

    /**
     * Get filtered asset count
     * Returns the total number of assets matching the provided filters
     * @param filterList - Array of filters to apply (optional - empty means count all)
     * @returns Observable of  number
     */
    abstract getAssetCount(filterList: Filter[]): Observable<number>;

    abstract saveGroupConfiguration(groupId: number): Observable<APIResponse<GroupResponseDto[]>>;

    abstract getGroupLabelList(): Observable<APIResponse<string[]>>;
    /**
     * Gets all form IDs matching current filters
     * Returns all available form IDs without pagination for "Select All" functionality
     * @returns Observable of APIResponse containing array of form IDs
     */
    abstract getAllFormIds(): Observable<APIResponse<number[]>>;
}
